import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  HiX,
  HiSearch,
  HiCollection,
  HiChevronDown,
  HiChevronRight,
  HiExternalLink,
  HiRefresh,
  HiEye
} from 'react-icons/hi';
import Notification from './Notification';
import { useNotification } from '../hooks/useNotification';
import { SkeletonTable } from './Skeleton';
import apiService from '../services/api';
import { normalizeApiData, normalizeImageUrl } from '../utils/apiHelpers';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
import { getStatusInfo, getStatusColor } from '../utils/statusConfig';
import DocumentsCompilacao from './DocumentsCompilacao';
import './Documents.css';

const getBoxInvoiceKey = (box) => {
  const id = box?.invoice_id ?? box?.invoice?.id ?? box?.fatura_id ?? null;
  if (id != null && id !== '') return String(id);
  const codigoInv = box?.codigo_invoice ?? box?.invoice?.codigo_invoice;
  if (codigoInv != null && String(codigoInv).trim() !== '') {
    return String(codigoInv).trim();
  }
  return null;
};

const getBoxInvoiceLabel = (box) => {
  const codigoInv = box?.codigo_invoice ?? box?.invoice?.codigo_invoice;
  if (codigoInv != null && String(codigoInv).trim() !== '') {
    return String(codigoInv).trim();
  }
  if (!getBoxInvoiceKey(box)) return null;
  const inv = box?.invoice;
  if (inv && typeof inv === 'object') {
    const s =
      inv.numero_documento ||
      inv.numero ||
      inv.codigo_rastreamento ||
      inv.codigo_invoice ||
      inv.title ||
      inv.tracking_code;
    if (s != null && String(s).trim() !== '') return String(s).trim();
    if (inv.id != null) return `#${inv.id}`;
  }
  const num = box?.invoice_numero ?? box?.numero_documento_invoice;
  if (num != null && String(num).trim() !== '') return String(num).trim();
  return `#${getBoxInvoiceKey(box)}`;
};

const getClienteNome = (box) =>
  box?.cliente_nome ||
  box?.cliente?.nome ||
  box?.cliente?.name ||
  box?.cliente_name ||
  (typeof box?.cliente === 'string' ? box.cliente : '') ||
  'Sem cliente';

const getClienteKey = (box) => {
  const id = box?.cliente_id ?? box?.cliente?.id;
  if (id != null && id !== '') return `id-${id}`;
  return `nome-${getClienteNome(box).toLowerCase()}`;
};

const getRemessaCodigo = (box) =>
  box?.codigo_pacote || box?.codigo || box?.codigo_caixa || `#${box?.id || '—'}`;

const getBoxDestino = (box) => {
  const raw =
    box?.destino ??
    box?.destination ??
    box?.invoice?.destino ??
    box?.invoice?.destination ??
    box?.tarifa?.destino ??
    box?.endereco_destino?.cidade ??
    box?.endereco_destino?.nome;
  if (raw == null || String(raw).trim() === '') return '';
  return String(raw).trim();
};

const getBoxCreatedAt = (box) =>
  box?.invoice?.created_at ||
  box?.invoice?.data ||
  box?.created_at ||
  box?.data_criacao ||
  box?.data ||
  null;

const getBoxCadastroMs = (box) =>
  getCreatedAtMs(box?.created_at || box?.data_criacao || box?.data || null);

const cadastroSortValue = (ms) => (ms > 0 ? ms : Number.MAX_SAFE_INTEGER);

const getCreatedAtMs = (value) => {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
};

const formatCreatedAt = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getBoxStatusRaw = (box) => {
  const inv = box?.invoice;
  if (inv && typeof inv === 'object') {
    if (typeof inv.status_invoice === 'string' && inv.status_invoice.trim()) {
      return inv.status_invoice.trim();
    }
    if (typeof inv.status === 'string' && inv.status.trim()) {
      return inv.status.trim();
    }
    if (inv.status && typeof inv.status === 'object') {
      const s =
        inv.status.status_invoice ||
        inv.status.nome ||
        inv.status.name ||
        inv.status.label;
      if (s != null && String(s).trim() !== '') return String(s).trim();
    }
  }
  if (typeof box?.status_invoice === 'string' && box.status_invoice.trim()) {
    return box.status_invoice.trim();
  }
  if (typeof box?.status === 'string' && box.status.trim()) {
    return box.status.trim();
  }
  return 'pedido_criado';
};

const getStatusLabel = (status) => {
  if (!status) return '—';
  const info = getStatusInfo(status);
  if (info && info.label && info.label !== status) return info.label;
  // status de invoice livre (ex.: "Nova", "Pendente")
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getNotaFiscalEntries = (box) => {
  const entries = [];
  const seen = new Set();

  const push = (rawUrl, extra = {}) => {
    if (rawUrl == null || typeof rawUrl !== 'string' || rawUrl.trim() === '') return;
    const url = normalizeImageUrl(rawUrl) || rawUrl.trim();
    if (!url) return;
    const key = url.split('?')[0];
    if (seen.has(key)) return;
    seen.add(key);
    entries.push({
      url,
      nome: extra.nome || 'Nota Fiscal',
      chave: extra.chave || '',
      id: extra.id ?? null,
      remessa: extra.remessa || getRemessaCodigo(box)
    });
  };

  const docs = Array.isArray(box?.notas_fiscais) ? box.notas_fiscais : [];
  docs.forEach((doc, index) => {
    push(doc?.arquivo_url || doc?.url, {
      id: doc?.id ?? null,
      chave: doc?.chave_acesso || '',
      nome: doc?.nome_arquivo || doc?.descricao || `Nota Fiscal ${index + 1}`
    });
  });

  const urls = Array.isArray(box?.nota_fiscal_url)
    ? box.nota_fiscal_url
    : box?.nota_fiscal_url
      ? [box.nota_fiscal_url]
      : [];
  urls.forEach((u, index) => {
    push(u, { nome: `Nota Fiscal ${index + 1}` });
  });

  if (typeof box?.nota_fiscal === 'string') {
    push(box.nota_fiscal, { nome: 'Nota Fiscal' });
  }

  return entries;
};

const getClienteNotasFiscais = (box, group) => {
  const clienteKey = getClienteKey(box);
  const siblings = (group?.remessas || []).filter((b) => getClienteKey(b) === clienteKey);
  const source = siblings.length > 0 ? siblings : [box];
  const entries = [];
  const seen = new Set();
  for (const remessa of source) {
    for (const nf of getNotaFiscalEntries(remessa)) {
      const key = nf.id != null ? `id-${nf.id}` : nf.url.split('?')[0];
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push(nf);
    }
  }
  return entries;
};

const isPdfUrl = (url) =>
  typeof url === 'string' &&
  (/\.pdf$/i.test(url) ||
    url.toLowerCase().includes('application/pdf') ||
    /\/pdf(\?|$)/i.test(url));

const isImageUrl = (url) => {
  if (typeof url !== 'string' || isPdfUrl(url)) return false;
  return (
    /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?|$)/i.test(url) ||
    url.startsWith('data:image/') ||
    url.toLowerCase().includes('image/')
  );
};

const displayCurrency = (value) => {
  if (value == null || value === '') return 'R$ 0,00';
  if (typeof value === 'string' && value.includes('R$')) return value;
  const number =
    typeof value === 'string'
      ? parseFloat(value.replace(/[^\d,.-]/g, '').replace(',', '.')) || 0
      : value || 0;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(number);
};

const groupRemessasByClienteCadastro = (remessas) => {
  const byClient = new Map();
  for (const box of remessas || []) {
    const key = getClienteKey(box);
    if (!byClient.has(key)) byClient.set(key, []);
    byClient.get(key).push(box);
  }

  const clientGroups = Array.from(byClient.values()).map((boxes) => {
    const sortedBoxes = boxes.slice().sort((a, b) => {
      const da = cadastroSortValue(getBoxCadastroMs(a));
      const db = cadastroSortValue(getBoxCadastroMs(b));
      if (da !== db) return da - db;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });
    const cadastroMs = sortedBoxes.reduce((min, box) => {
      const ms = getBoxCadastroMs(box);
      if (!ms) return min;
      return min === Number.MAX_SAFE_INTEGER ? ms : Math.min(min, ms);
    }, Number.MAX_SAFE_INTEGER);
    return { boxes: sortedBoxes, cadastroMs };
  });

  clientGroups.sort((a, b) => {
    const da = cadastroSortValue(a.cadastroMs);
    const db = cadastroSortValue(b.cadastroMs);
    if (da !== db) return da - db;
    return getClienteNome(a.boxes[0]).localeCompare(getClienteNome(b.boxes[0]), 'pt-BR');
  });

  return clientGroups;
};

const sortRemessasByClienteCadastro = (remessas) =>
  groupRemessasByClienteCadastro(remessas).flatMap((g) => g.boxes);

const Documents = () => {
  const [activeTab, setActiveTab] = useState('remessas');

  // Remessas / NF / Compilação
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePerPage, setInvoicePerPage] = useState(10);
  const [selectedBoxItems, setSelectedBoxItems] = useState(null);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedNotasFiscais, setSelectedNotasFiscais] = useState([]);
  const [selectedNotaFiscalIndex, setSelectedNotaFiscalIndex] = useState(0);
  const [notaFiscalMeta, setNotaFiscalMeta] = useState(null);
  const [showNotaFiscalModal, setShowNotaFiscalModal] = useState(false);
  const { notifications, removeNotification, showWarning, showSuccess } = useNotification();

  const loadPackages = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoadingPackages(true);
    try {
      const fetchPerPage = 100;
      const all = [];
      let page = 1;
      for (;;) {
        const response = await apiService.getBoxes({
          page,
          per_page: fetchPerPage
        });
        const list = normalizeApiData(response);
        if (!Array.isArray(list) || list.length === 0) break;
        all.push(...list);
        const lastPage = Number(response?.last_page ?? response?.lastPage ?? 1);
        if (page >= lastPage) break;
        if (list.length < fetchPerPage) break;
        page += 1;
      }
      setPackages(all);
    } catch (error) {
      console.error('Erro ao carregar remessas:', error);
      setPackages([]);
      showWarning(error.message || 'Erro ao carregar remessas');
    } finally {
      setLoadingPackages(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- showWarning is unstable
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useRealtimeRefresh(
    ['packages', 'products', 'documents', 'invoices'],
    () => loadPackages({ silent: true })
  );

  const invoiceGroups = useMemo(() => {
    const map = new Map();
    for (const box of packages) {
      const key = getBoxInvoiceKey(box);
      const label = getBoxInvoiceLabel(box);
      // Sem invoice atrelado → não exibe
      if (!key && !label) continue;

      const groupKey = key || label;
      if (!map.has(groupKey)) {
        map.set(groupKey, {
          key: groupKey,
          label: label || `#${groupKey}`,
          remessas: []
        });
      }
      map.get(groupKey).remessas.push(box);
    }

    const groups = Array.from(map.values()).map((group) => {
      const remessas = sortRemessasByClienteCadastro(group.remessas);

      const clientes = new Set(remessas.map((b) => getClienteKey(b)));

      let destino = '';
      for (const box of remessas) {
        const d = getBoxDestino(box);
        if (d) {
          destino = d;
          break;
        }
      }

      let createdAt = null;
      let createdAtMs = 0;
      for (const box of remessas) {
        const raw = getBoxCreatedAt(box);
        const ms = getCreatedAtMs(raw);
        if (ms > createdAtMs) {
          createdAtMs = ms;
          createdAt = raw;
        }
      }

      let status = 'pedido_criado';
      let bestOrder = -1;
      for (const box of remessas) {
        const raw = getBoxStatusRaw(box);
        const info = getStatusInfo(raw);
        const order = info?.order ?? 0;
        if (order > bestOrder) {
          bestOrder = order;
          status = raw;
        } else if (bestOrder < 0) {
          status = raw;
          bestOrder = 0;
        }
      }

      return {
        ...group,
        remessas,
        clientesCount: clientes.size,
        destino: destino || '—',
        createdAt,
        createdAtMs,
        status
      };
    });

    // Mais novo no topo
    groups.sort((a, b) => {
      if (b.createdAtMs !== a.createdAtMs) return b.createdAtMs - a.createdAtMs;
      return a.label.localeCompare(b.label, 'pt-BR');
    });
    return groups;
  }, [packages]);

  const filteredGroups = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    if (!q) return invoiceGroups;

    return invoiceGroups
      .map((group) => {
        const invoiceMatch =
          (group.label || '').toLowerCase().includes(q) ||
          (group.destino || '').toLowerCase().includes(q) ||
          getStatusLabel(group.status).toLowerCase().includes(q) ||
          (group.status || '').toLowerCase().includes(q);

        const remessas = group.remessas.filter((box) => {
          if (invoiceMatch) return true;
          const cliente = getClienteNome(box).toLowerCase();
          const email = (box?.cliente?.email || box?.cliente_email || '').toLowerCase();
          const codigo = String(getRemessaCodigo(box)).toLowerCase();
          const destino = getBoxDestino(box).toLowerCase();
          const itens = Array.isArray(box.itens) ? box.itens : [];
          const itemMatch = itens.some((item) => {
            const nome = (item.nome || item.name || '').toLowerCase();
            const desc = (item.descricao || item.description || '').toLowerCase();
            const ncm = (item.ncm || '').toLowerCase();
            return nome.includes(q) || desc.includes(q) || ncm.includes(q);
          });
          return (
            cliente.includes(q) ||
            email.includes(q) ||
            codigo.includes(q) ||
            destino.includes(q) ||
            itemMatch
          );
        });

        if (!invoiceMatch && remessas.length === 0) return null;
        return { ...group, remessas: invoiceMatch ? group.remessas : remessas };
      })
      .filter(Boolean);
  }, [invoiceGroups, searchTerm]);

  const invoiceTotalPages = Math.max(1, Math.ceil(filteredGroups.length / invoicePerPage));
  const invoiceStartIndex = (invoicePage - 1) * invoicePerPage;
  const paginatedInvoiceGroups = filteredGroups.slice(
    invoiceStartIndex,
    invoiceStartIndex + invoicePerPage
  );

  useEffect(() => {
    setInvoicePage(1);
  }, [searchTerm, invoicePerPage]);

  useEffect(() => {
    if (invoicePage > invoiceTotalPages) {
      setInvoicePage(invoiceTotalPages);
    }
  }, [invoicePage, invoiceTotalPages]);

  useEffect(() => {
    if (!searchTerm.trim()) return;
    setExpandedInvoices((prev) => {
      const next = { ...prev };
      paginatedInvoiceGroups.forEach((g) => {
        next[g.key] = true;
      });
      return next;
    });
  }, [searchTerm, paginatedInvoiceGroups]);

  const toggleInvoice = (key) => {
    setExpandedInvoices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const closeNotaFiscal = () => {
    setShowNotaFiscalModal(false);
    setSelectedNotasFiscais([]);
    setSelectedNotaFiscalIndex(0);
    setNotaFiscalMeta(null);
  };

  const openNotaFiscal = (box, group) => {
    const notas = getClienteNotasFiscais(box, group);
    if (notas.length === 0) {
      showWarning('Nota fiscal não disponível para este cliente');
      return;
    }
    setSelectedNotasFiscais(notas);
    setSelectedNotaFiscalIndex(0);
    setNotaFiscalMeta({
      cliente: getClienteNome(box),
      invoice: getBoxInvoiceLabel(box)
    });
    setShowNotaFiscalModal(true);
  };

  const openItems = (box) => {
    setSelectedBoxItems(box);
    setShowItemsModal(true);
  };

  return (
    <div className="page-container documents-page">
      <div className="breadcrumb">
        <span>Painel</span>
        <span>Central Fiscal</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Central Fiscal</h1>
          <p>Notas fiscais, remessas e compilação da invoice — chave de acesso, CFOP, NCM e valores</p>
        </div>
        <div className="documents-header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={loadPackages}
            disabled={loadingPackages}
          >
            <HiRefresh size={18} />
            Atualizar
          </button>
        </div>
      </div>

      <div className="documents-tabs">
        <button
          type="button"
          className={`documents-tab ${activeTab === 'remessas' ? 'active' : ''}`}
          onClick={() => setActiveTab('remessas')}
        >
          Remessas / NF
        </button>
        <button
          type="button"
          className={`documents-tab ${activeTab === 'compilacao' ? 'active' : ''}`}
          onClick={() => setActiveTab('compilacao')}
        >
          Compilação
        </button>
      </div>

      {activeTab === 'remessas' && (
        <>
          <div className="page-actions">
            <div className="search-box">
              <HiSearch className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Buscar por Invoice, destino, cliente, remessa ou item..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="card">
            {loadingPackages ? (
              <SkeletonTable rows={8} columns={5} />
            ) : filteredGroups.length === 0 ? (
              <div className="empty-state">
                <p>
                  {searchTerm
                    ? 'Nenhuma remessa encontrada para a busca'
                    : 'Nenhuma remessa com Invoice encontrada'}
                </p>
              </div>
            ) : (
              <>
                <div className="documents-client-list">
                  {paginatedInvoiceGroups.map((group) => {
                    const expanded = !!expandedInvoices[group.key];
                    const totalItens = group.remessas.reduce(
                      (sum, box) => sum + (Array.isArray(box.itens) ? box.itens.length : 0),
                      0
                    );
                    const nfCount = new Set(
                      group.remessas.flatMap((b) =>
                        getNotaFiscalEntries(b).map((n) => n.id ?? n.url)
                      )
                    ).size;
                    const statusLabel = getStatusLabel(group.status);
                    const statusColor = getStatusColor(group.status) || '#6b7280';

                    return (
                      <div key={group.key} className="documents-client-group">
                        <button
                          type="button"
                          className="documents-client-header"
                          onClick={() => toggleInvoice(group.key)}
                        >
                          <span className="documents-client-chevron">
                            {expanded ? <HiChevronDown size={20} /> : <HiChevronRight size={20} />}
                          </span>
                          <div className="documents-client-info">
                            <strong>
                              Invoice{' '}
                              <span className="documents-invoice-code">{group.label}</span>
                            </strong>
                            <div className="documents-invoice-title-meta">
                              <span>
                                <span className="documents-meta-label">Destino:</span>{' '}
                                {group.destino}
                              </span>
                              <span className="documents-meta-sep">·</span>
                              <span>
                                <span className="documents-meta-label">Criado:</span>{' '}
                                {formatCreatedAt(group.createdAt)}
                              </span>
                              <span className="documents-meta-sep">·</span>
                              <span
                                className="documents-status-pill"
                                style={{
                                  color: statusColor,
                                  background: `${statusColor}18`,
                                  borderColor: `${statusColor}40`
                                }}
                              >
                                {statusLabel}
                              </span>
                            </div>
                          </div>
                          <div className="documents-client-meta">
                            <span className="badge badge-secondary">
                              {group.clientesCount} cliente
                              {group.clientesCount !== 1 ? 's' : ''}
                            </span>
                            <span className="badge badge-secondary">
                              {group.remessas.length} remessa
                              {group.remessas.length !== 1 ? 's' : ''}
                            </span>
                            <span className="badge badge-info">
                              {totalItens} ite{totalItens !== 1 ? 'ns' : 'm'}
                            </span>
                            <span
                              className={`badge ${nfCount > 0 ? 'badge-success' : 'badge-secondary'}`}
                            >
                              {nfCount} NF
                            </span>
                          </div>
                        </button>

                        {expanded && (
                          <div className="table-container documents-remessa-table-wrap">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Cliente</th>
                                  <th>Remessa</th>
                                  <th>Itens</th>
                                  <th>NF</th>
                                  <th>Ações</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.remessas.map((box) => {
                                  const itensCount = Array.isArray(box.itens)
                                    ? box.itens.length
                                    : 0;
                                  const clienteNfs = getClienteNotasFiscais(box, group);

                                  return (
                                    <tr
                                      key={
                                        box.id ||
                                        `${getRemessaCodigo(box)}-${getClienteKey(box)}`
                                      }
                                    >
                                      <td>
                                        <strong>{getClienteNome(box)}</strong>
                                        {(box?.cliente?.email || box?.cliente_email) && (
                                          <div className="documents-client-email">
                                            {box?.cliente?.email || box?.cliente_email}
                                          </div>
                                        )}
                                      </td>
                                      <td>
                                        <strong>{getRemessaCodigo(box)}</strong>
                                      </td>
                                      <td>
                                        <span className="badge badge-info">{itensCount}</span>
                                      </td>
                                      <td>
                                        {clienteNfs.length > 0 ? (
                                          <button
                                            type="button"
                                            className="btn-icon btn-view documents-nf-open"
                                            onClick={() => openNotaFiscal(box, group)}
                                            title={
                                              clienteNfs.length > 1
                                                ? `Ver ${clienteNfs.length} notas fiscais do cliente`
                                                : 'Ver Nota Fiscal'
                                            }
                                          >
                                            <HiEye size={18} />
                                            {clienteNfs.length > 1 && (
                                              <span className="documents-nf-count">
                                                {clienteNfs.length}
                                              </span>
                                            )}
                                          </button>
                                        ) : (
                                          <span
                                            className="btn-icon"
                                            title="Nota Fiscal não disponível"
                                            style={{
                                              color: '#9ca3af',
                                              cursor: 'not-allowed',
                                              opacity: 0.5
                                            }}
                                          >
                                            <HiEye size={18} />
                                          </span>
                                        )}
                                      </td>
                                      <td>
                                        <div className="actions">
                                          <button
                                            type="button"
                                            className="btn-icon"
                                            onClick={() => openItems(box)}
                                            title="Listar itens"
                                            style={{ color: '#6366f1' }}
                                          >
                                            <HiCollection size={18} />
                                          </button>
                                          {clienteNfs.length > 0 && (
                                            <button
                                              type="button"
                                              className="btn-icon btn-download"
                                              onClick={() => openNotaFiscal(box, group)}
                                              title={
                                                clienteNfs.length > 1
                                                  ? 'Abrir todas as NFs do cliente'
                                                  : 'Abrir NF'
                                              }
                                            >
                                              <HiExternalLink size={18} />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="table-footer">
                  <div className="pagination-info">
                    {filteredGroups.length > 0 ? (
                      <span>
                        Mostrando {invoiceStartIndex + 1} a{' '}
                        {Math.min(invoiceStartIndex + invoicePerPage, filteredGroups.length)} de{' '}
                        {filteredGroups.length} invoice
                        {filteredGroups.length !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span>Nenhum registro encontrado</span>
                    )}
                  </div>
                  <div className="items-per-page">
                    <span>Mostrar:</span>
                    <select
                      value={invoicePerPage}
                      onChange={(e) => {
                        setInvoicePerPage(Number(e.target.value));
                        setInvoicePage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <div className="pagination">
                    <button
                      type="button"
                      className="pagination-btn"
                      onClick={() => setInvoicePage((prev) => Math.max(1, prev - 1))}
                      disabled={invoicePage === 1}
                    >
                      ‹
                    </button>
                    <span className="pagination-info">
                      Página {invoicePage} de {invoiceTotalPages}
                    </span>
                    <button
                      type="button"
                      className="pagination-btn"
                      onClick={() =>
                        setInvoicePage((prev) => Math.min(invoiceTotalPages, prev + 1))
                      }
                      disabled={invoicePage === invoiceTotalPages}
                    >
                      ›
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {activeTab === 'compilacao' && (
        <DocumentsCompilacao
          invoiceGroups={invoiceGroups}
          loadingPackages={loadingPackages}
          showWarning={showWarning}
          showSuccess={showSuccess}
        />
      )}

      {/* Modal de Itens da Remessa */}
      {showItemsModal && selectedBoxItems && (
        <div className="modal-overlay" onClick={() => setShowItemsModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>
                  Itens — {getRemessaCodigo(selectedBoxItems)}
                </h2>
                <p className="documents-modal-subtitle">
                  {getClienteNome(selectedBoxItems)}
                  {getBoxInvoiceLabel(selectedBoxItems)
                    ? ` · Invoice ${getBoxInvoiceLabel(selectedBoxItems)}`
                    : ''}
                </p>
              </div>
              <button
                className="modal-close"
                onClick={() => {
                  setShowItemsModal(false);
                  setSelectedBoxItems(null);
                }}
              >
                <HiX size={24} />
              </button>
            </div>
            <div className="modal-body">
              {selectedBoxItems.itens && selectedBoxItems.itens.length > 0 ? (
                <div className="items-list">
                  {selectedBoxItems.itens.map((item, index) => (
                    <div key={item.id || index} className="item-detail-card">
                      <div className="item-detail-header">
                        <div className="item-detail-info">
                          <h4>{item.nome || item.name || `Item ${index + 1}`}</h4>
                          <div className="item-detail-desc">
                            {item.ncm && (
                              <span>
                                <strong>NCM:</strong> {item.ncm}
                              </span>
                            )}
                            {item.ncm && item.descricao && <span> | </span>}
                            {(item.descricao || item.description) && (
                              <span>
                                <strong>Descrição:</strong> {item.descricao || item.description}
                              </span>
                            )}
                          </div>
                          <div className="item-detail-meta">
                            <span>
                              <strong>Quantidade:</strong>{' '}
                              {item.quantidade || item.quantity || 'N/A'}
                            </span>
                            <span>
                              <strong>Peso:</strong> {item.peso || item.weight || 'N/A'}
                              {item.peso || item.weight ? ' kg' : ''}
                            </span>
                            <span>
                              <strong>Preço:</strong>{' '}
                              {item.preco
                                ? displayCurrency(item.preco)
                                : item.price
                                  ? displayCurrency(item.price)
                                  : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                      {item.fotos && Array.isArray(item.fotos) && item.fotos.length > 0 && (
                        <div className="item-detail-photos">
                          <label>Fotos do Item ({item.fotos.length}):</label>
                          <div className="photo-gallery">
                            {item.fotos.map((photo, photoIdx) => {
                              const rawPhotoUrl =
                                photo?.url ||
                                photo?.preview ||
                                (typeof photo === 'string' ? photo : null);
                              const photoId = photo?.id || `item-${index}-photo-${photoIdx}`;
                              if (!rawPhotoUrl) return null;
                              const photoUrl = normalizeImageUrl(rawPhotoUrl);
                              return (
                                <div key={photoId} className="photo-gallery-item">
                                  <img
                                    src={photoUrl}
                                    alt={`Foto ${photoIdx + 1} do item ${item.nome || item.name || ''}`}
                                    onClick={() => window.open(photoUrl, '_blank')}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                  <div className="photo-overlay">
                                    <button
                                      type="button"
                                      className="photo-view-btn"
                                      onClick={() => window.open(photoUrl, '_blank')}
                                      title="Abrir em nova aba"
                                    >
                                      <HiEye size={18} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <p>Nenhum item cadastrado nesta remessa</p>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowItemsModal(false);
                  setSelectedBoxItems(null);
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nota Fiscal */}
      {showNotaFiscalModal && selectedNotasFiscais.length > 0 && (
        <div className="modal-overlay" onClick={closeNotaFiscal}>
          <div
            className="modal-content modal-large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div>
                <h2>
                  {selectedNotasFiscais.length > 1
                    ? `Notas Fiscais (${selectedNotasFiscais.length})`
                    : 'Nota Fiscal'}
                </h2>
                {notaFiscalMeta && (
                  <p className="documents-modal-subtitle">
                    {notaFiscalMeta.cliente}
                    {notaFiscalMeta.invoice ? ` · Invoice ${notaFiscalMeta.invoice}` : ''}
                  </p>
                )}
              </div>
              <button className="modal-close" onClick={closeNotaFiscal}>
                <HiX size={24} />
              </button>
            </div>
            {selectedNotasFiscais.length > 1 && (
              <div className="documents-nf-tabs" role="tablist">
                {selectedNotasFiscais.map((nf, index) => {
                  const chaveShort =
                    nf.chave && String(nf.chave).replace(/\D/g, '').length >= 8
                      ? String(nf.chave).replace(/\D/g, '').slice(-8)
                      : '';
                  return (
                    <button
                      key={nf.id || nf.url}
                      type="button"
                      role="tab"
                      aria-selected={index === selectedNotaFiscalIndex}
                      className={`documents-nf-tab ${
                        index === selectedNotaFiscalIndex ? 'is-active' : ''
                      }`}
                      onClick={() => setSelectedNotaFiscalIndex(index)}
                      title={nf.chave || nf.nome}
                    >
                      NF {index + 1}
                      {nf.remessa ? ` · ${nf.remessa}` : ''}
                      {chaveShort ? ` · ${chaveShort}` : ''}
                    </button>
                  );
                })}
              </div>
            )}
            {(() => {
              const atual = selectedNotasFiscais[selectedNotaFiscalIndex];
              if (!atual) return null;
              return (
                <>
                  <p className="documents-nf-current-meta">
                    {atual.nome}
                    {atual.remessa ? ` · Remessa ${atual.remessa}` : ''}
                    {atual.chave ? ` · ${atual.chave}` : ''}
                  </p>
                  <div className="document-viewer">
                    {isImageUrl(atual.url) ? (
                      <img src={atual.url} alt={atual.nome || 'Nota Fiscal'} />
                    ) : (
                      <iframe
                        src={atual.url}
                        title={atual.nome || 'Nota Fiscal'}
                        style={{ width: '100%', height: '600px', border: 'none' }}
                      />
                    )}
                  </div>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeNotaFiscal}
                    >
                      Fechar
                    </button>
                    <a
                      href={atual.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{ textDecoration: 'none' }}
                    >
                      <HiExternalLink size={18} />
                      Abrir em nova aba
                    </a>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      <div className="notifications-container">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Documents;
